import { Add } from "@mui/icons-material";
import { Box, ButtonBase, Chip, Divider, Skeleton, Stack, styled, Typography } from "@mui/material";
import React from "react";
import { Association, AssociationObjectDetails } from "../../interfaces";
import { useAppDispatch } from "../../redux";
import { DtTemplateType, useCreateDataTableMutation, useLinkModuleMutation, useUnlinkModuleMutation } from "../../redux/services";
import { closeBackdrop, openBackdrop } from "../../redux/slices/backdrop";
import { closeDialog, openDialog } from "../../redux/slices/dialog";
import { AppRoutes } from "../../router/routes";
import { AddEditDocument } from "../documents/addEditDocument";
import { LinkUnlinkDocumentTemplate } from "./linkUnlinkDocumentTemplate";
import { LinkUnlinkEntity } from "./linkUnlinkEntity";

const HeadingButton = styled(ButtonBase)(({ theme }) => ({
    fontSize: 12,
    width: '100%',
    textAlign: 'left',
    paddingTop: '4px',
    paddingBottom: '2px',
    borderTopRightRadius: '4px',
    borderTopLeftRadius: '4px',
    fontFamily: 'htrts_regular',
    '&:hover,&:focus': {
        fontFamily: 'htrts_medium',
        paddingLeft: '4px',
        paddingRight: '4px',
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.primary.main,
    },
    '& span': {
        width: '100%',
    },
    '& svg': {
        width: 16,
        height: 16,
    },
}));

export const ModuleMapperStack: React.FC<{
    variant: 'bom' | 'document' | 'material' | 'Checklist Form',
    title: string,
    buttonName?: string,
    hideBtn?: boolean,
    objectType: Association['object_type'],
    objectId: string,
    parentObjectType: Association['object_type'],
    parentObjectId: string,
    linkUnlinkObjectType: Association['object_type'],
    stacks: AssociationObjectDetails[],
    onUpdate?: (itemsAdded: AssociationObjectDetails[], itemsRemoved: AssociationObjectDetails[]) => void,
    hideHeader?: boolean,
    hideBorder?: boolean,
    loading?: boolean,
    enableNavigation?: boolean
}> = ({
    variant,
    title = '',
    stacks = [],
    objectId,
    objectType,
    parentObjectId,
    parentObjectType,
    buttonName = '',
    hideBtn = false,
    linkUnlinkObjectType,
    onUpdate = (itemsAdded, itemsRemoved) => false,
    hideBorder = false,
    hideHeader = false,
    loading = false,
    enableNavigation = false
}) => {

        const dispatch = useAppDispatch();

        const [link] = useLinkModuleMutation();
        const [unLink] = useUnlinkModuleMutation();
        const [createForm] = useCreateDataTableMutation();

        const [stack, setStack] = React.useState<AssociationObjectDetails[]>(stacks);

        const updateStack = async (links: AssociationObjectDetails[] | any, unlinks: AssociationObjectDetails[]) => {

            if (links.length) {

                let linkIds = (links as AssociationObjectDetails[]).map(_ => _._id);

                if (variant === 'Checklist Form' && links.length > 0) {
                    dispatch(openBackdrop('Creating form...'));
                    let response = await createForm({
                        dtTemplateType: 'checklist',
                        dtInstanceType: "form",
                        dtTemplateId: links?.[0]?._id ?? "",
                        payload: {
                            name: links?.[0]?.name ?? "",
                            description: links?.[0]?.description || undefined,
                            tables: links?.[0]?.tables?.map((table: any, index: number) => {
                                return {
                                    name: table.name,
                                    description: table.description,
                                    position: index,
                                    rows: table?.rows ?? [],
                                    header_color: table?.header_color,
                                    columns: table?.columns?.filter((col: any) => col.field)?.map((col: any) => {
                                        return {
                                            ...col,
                                            header_class_name: col.header_class_name,
                                            header_name: col.header_name,
                                            editable: col?.editable ? true : false
                                        };
                                    }) ?? [],
                                }
                            }) ?? [],
                        } as any
                    }).unwrap();

                    linkIds = [response._id];
                    links = [...stack, response];
                    dispatch(closeBackdrop());
                }

                link({
                    object_id: objectId, object_type: objectType, payload: {
                        links: [{
                            object_ids: linkIds,
                            object_type: linkUnlinkObjectType
                        }]
                    }
                });
            };

            if (unlinks.length) {
                unLink({
                    object_id: objectId, object_type: objectType, payload: {
                        unlinks: [{
                            object_ids: (unlinks as AssociationObjectDetails[]).map(_ => _._id),
                            object_type: linkUnlinkObjectType
                        }]
                    }
                });
            };


            setStack(links);
            onUpdate(links, unlinks);

            dispatch(closeDialog());
        };

        const addEditStack = () => {
            let body = <></>;

            let dtVariantToForm: { [key: string]: DtTemplateType } = {
                'Checklist Form': 'checklist'
            };

            if (variant === 'Checklist Form') {
                body = <LinkUnlinkDocumentTemplate
                    multiple={false}
                    dtType={dtVariantToForm[variant]}
                    documentTemplates={null}
                    onUpdate={updateStack}
                    objectId={parentObjectId}
                    objectType={parentObjectType}
                />;
            } else {
                body = <LinkUnlinkEntity
                    entity={variant}
                    items={stack}
                    onUpdate={updateStack}
                    objectId={parentObjectId}
                    objectType={parentObjectType}
                />
            }

            dispatch(openDialog({
                title: `${variant === 'Checklist Form' ? `Create Form from ${dtVariantToForm[variant]} template` : `Link/Unlink ${buttonName}`}`,
                body,
                hidePositiveBtn: true,
                hideNegativeBtn: true,
            }));
        };

        const removeStack = (index: number) => {
            let newStack = stack;
            let removedStack = newStack.splice(index, 1);
            unLink({
                object_id: objectId, object_type: objectType, payload: {
                    unlinks: [{
                        object_ids: (removedStack as AssociationObjectDetails[]).map(_ => _._id),
                        object_type: linkUnlinkObjectType
                    }]
                }
            });
            setStack(newStack);
            onUpdate(newStack, removedStack);
        };

        const navigateToEntity = (entity: AssociationObjectDetails) => {
            if (enableNavigation) {
                // Navigate to the entity
                switch (linkUnlinkObjectType) {
                    case 'bom':
                        window.open(AppRoutes.viewBomBasicDetail(entity._id), '_blank');
                        break;
                    case 'material':
                        window.open(AppRoutes.viewMatBasicDetail(entity._id), '_blank');
                        break;
                    case 'document':
                        dispatch(openDialog({
                            title: "",
                            hideNegativeBtn: true,
                            hidePositiveBtn: true,
                            enablePadding: false,
                            body: <AddEditDocument
                                isEdit={true}
                                data={entity}
                                objectType={parentObjectType}
                                refetch={() => false}
                                _id={parentObjectId}
                                hideUpload={false}
                                showFileName={false}
                                showModuleMapper={false}
                                showBOM={false}
                                showDocumentActions={true}
                            />
                        }));
                        break;
                    default:

                        break;
                }
            }
        };

        React.useEffect(() => {
            setStack(stacks);
            // eslint-disable-next-line
        }, [stacks]);

        return <Box>

            {!hideHeader && <>
                <HeadingButton onClick={() => addEditStack()}>
                    <span>{title}</span>
                    <Add fontSize='small' />
                </HeadingButton>

                <Divider sx={{ mt: '1px' }} />
            </>}

            <Box sx={{ m: hideBorder ? 0 : 1, display: "flex", justifyContent: "flex-start" }}>
                <Stack direction={"row"} flexWrap={"wrap"} gap={1}>
                    {stack.map((stack, index) => <Chip
                        variant="outlined"
                        onClick={() => navigateToEntity(stack)}
                        color="primary"
                        label={`${stack.sequence_id}-r${stack?.version ?? 0}`}
                        onDelete={() => removeStack(index)}
                        key={index}
                        size="small"
                        sx={{
                            fontSize: 12
                        }}
                    />)}

                    {loading && <>
                        <Skeleton width={"50px"} />
                        <Skeleton width={"50px"} />
                        <Skeleton width={"50px"} />
                    </>}

                    {!loading && stack.length === 0 && <Typography variant="caption" sx={{ fontSize: 10 }} color="textSecondary">Yet to link {variant}</Typography>}
                </Stack>
            </Box>
        </Box>
    }